// Custom Imports
// =========================================================
import { FormatKey } from 'src/app/utils/global_functions'
import { CellClassRules } from '../02_global-settings/cell-class-rules'
import { GenerateDefaultColDefs } from '../02_global-settings/global-cell-formatter'
import {
  type ValueGetterParams,
  type ValueFormatterParams,
  type CellClickedEvent,
  type ICellRendererParams,
  type EditableCallbackParams
} from 'ag-grid-community'
import { ButtonCellRenderer } from '../03_custom-components/button-cell-renderer/button-renderer'
import { filter } from 'lodash'

export interface RecoBuyQty_GridDataParams {
  mainColDefs: any[]
  // detialColDefs: any[];
}

export async function RecoBuyQty_GenerateGridData (params: RecoBuyQty_GridDataParams) {
  const {
    mainColDefs
    // detailColDefs
  } = params

  const GenerateCols = async (data: any[], gridToRender: string) => {
    const mappedCols = {}

    Object.entries(data).map(([colKey, colVal]) => {
      const colId = colVal.refData?.custom_field || FormatKey(colKey)

      const obj = {
        ...GenerateDefaultColDefs(colKey, { colId, ...colVal })
      }
      Object.assign(mappedCols, {
        [obj.colId]: RecoBuyQty_customCellFormatting(obj, gridToRender)
      })
    })

    return Object.values(mappedCols)
  }

  const getResults = async () => {
    let mainCols = await GenerateCols(mainColDefs, 'main')
    mainCols = mainCols.map((col: any) => {
      if (col.colId === 'ecomm_reco_qty' || col.colId === 'stores_reco_qty' || col.colId === 'total_reco_qty') {
        return { ...col, cellClass: 'bold-cell' }
      }
      return col
    })
    const results = await Promise.all([mainCols])
    const m = results.indexOf(mainCols)
    const data = {
      mainColDefs: results[m]
    }
    return data
  }
  return await getResults()
}

export const RecoBuyQty_customCellFormatting = (col, gridToRender) => {
  const hasValue = val => val && ((val !== '' && val !== undefined) || val !== null)

  const getParams = () => {
    // Main Grid Settings
    if (gridToRender === 'main') {
      switch (col.colId) {
        case 'moq':
        case 'ecomm_tgt_qty':
        case 'stores_tgt_qty':
        case 'ecomm_reco_qty':
        case 'stores_reco_qty':
          return {
            editable: (params: EditableCallbackParams) => !params.data.locked,
            valueGetter: (params: ValueGetterParams) => {
              return params.data[col.colId]
            },
            valueFormatter: (params: ValueFormatterParams) => {
              return Math.round(params.value).toLocaleString('en-US')
            },
            getQuickFilterText: params => {
              return Math.round(params.data[col.colId])
            }
          }
        case 'total_tgt_qty':
          return {
            valueGetter: (params: ValueGetterParams) => {
              return params.data['ecomm_tgt_qty'] + params.data['stores_tgt_qty']
            },
            valueFormatter: (params: ValueFormatterParams) => {
              return Math.round(params.value).toLocaleString('en-US')
            },
            getQuickFilterText: params => {
              return Math.round(params.data['ecomm_tgt_qty'] + params.data['stores_tgt_qty'])
            }
          }
        case 'total_demand_qty':
          return {
            valueGetter: (params: ValueGetterParams) => {
              return params.data['ecomm_demand_qty'] + params.data['stores_demand_qty']
            },
            valueFormatter: (params: ValueFormatterParams) => {
              return Math.round(params.value).toLocaleString('en-US')
            },
            getQuickFilterText: params => {
              return Math.round(params.data['ecomm_demand_qty'] + params.data['stores_demand_qty'])
            }
          }
        case 'total_reco_qty':
          return {
            valueGetter: (params: ValueGetterParams) => {
              return params.data['ecomm_reco_qty'] + params.data['stores_reco_qty']
            },
            valueFormatter: (params: ValueFormatterParams) => {
              return Math.round(params.value).toLocaleString('en-US')
            },
            getQuickFilterText: params => {
              return Math.round(params.data['ecomm_reco_qty'] + params.data['stores_reco_qty'])
            }
          }
        case 'ecomm_reco_cost_($)':
          return {
            valueGetter: (params: ValueGetterParams) => {
              return params.data['ecomm_reco_qty'] * params.data['retail_cost_($)']
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.data?.__isTotalRow) return '$' + params.data[col.colId].toLocaleString('en-US')
              if (params.value == null || params.value === '') return '$0'
              const num = typeof params.value === 'string' ? parseFloat(params.value) : params.value
              if (isNaN(num)) return '$0'
              return '$' + Math.round(num).toLocaleString('en-US')
            },
            getQuickFilterText: params => {
              return Math.round(params.data['ecomm_reco_qty'] * params.data['retail_cost_($)'])
            },
            filterValueGetter: params => {
              return Math.round(params.data['ecomm_reco_qty'] * params.data['retail_cost_($)'])
            }
          }
        case 'stores_reco_cost_($)':
          return {
            valueGetter: (params: ValueGetterParams) => {
              return params.data['stores_reco_qty'] * params.data['retail_cost_($)']
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.data?.__isTotalRow) return '$' + params.data[col.colId].toLocaleString('en-US')
              if (params.value == null || params.value === '') return '$0'
              const num = typeof params.value === 'string' ? parseFloat(params.value) : params.value
              if (isNaN(num)) return '$0'
              return '$' + Math.round(num).toLocaleString('en-US')
            },
            getQuickFilterText: params => {
              return Math.round(params.data['stores_reco_qty'] * params.data['retail_cost_($)'])
            },
            filterValueGetter: params => {
              return Math.round(params.data['stores_reco_qty'] * params.data['retail_cost_($)'])
            }
          }
        case 'total_reco_cost_($)':
          return {
            valueGetter: (params: ValueGetterParams) => {
              // depending on other calculations doesn't always work
              return (
                (params.data['ecomm_reco_qty'] + params.data['stores_reco_qty']) *
                                params.data['retail_cost_($)']
              )
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.data?.__isTotalRow) return '$' + params.data[col.colId].toLocaleString('en-US')
              if (params.value == null || params.value === '') return '$0'
              const num = typeof params.value === 'string' ? parseFloat(params.value) : params.value
              if (isNaN(num)) return '$0'
              return '$' + Math.round(num).toLocaleString('en-US')
            },
            getQuickFilterText: params => {
              return Math.round(
                params.data['ecomm_reco_qty'] * params.data['retail_cost_($)'] +
                                    params.data['stores_reco_qty'] * params.data['retail_cost_($)']
              )
            },
            filterValueGetter: params => {
              return Math.round(
                params.data['ecomm_reco_qty'] * params.data['retail_cost_($)'] +
                                    params.data['stores_reco_qty'] * params.data['retail_cost_($)']
              )
            }
          }
        case 'ecomm_reco_price_($)':
          return {
            valueGetter: (params: ValueGetterParams) => {
              return params.data['ecomm_reco_qty'] * params.data['retail_price_($)']
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.data?.__isTotalRow) return '$' + params.data[col.colId].toLocaleString('en-US')
              if (params.value == null || params.value === '') return '$0'
              const num = typeof params.value === 'string' ? parseFloat(params.value) : params.value
              if (isNaN(num)) return '$0'
              return '$' + Math.round(num).toLocaleString('en-US')
            },
            getQuickFilterText: params => {
              return Math.round(params.data['ecomm_reco_qty'] * params.data['retail_price_($)'])
            },
            filterValueGetter: params => {
              return Math.round(params.data['ecomm_reco_qty'] * params.data['retail_price_($)'])
            }
          }
        case 'stores_reco_price_($)':
          return {
            valueGetter: (params: ValueGetterParams) => {
              return params.data['stores_reco_qty'] * params.data['retail_price_($)']
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.data?.__isTotalRow) return '$' + params.data[col.colId].toLocaleString('en-US')
              if (params.value == null || params.value === '') return '$0'
              const num = typeof params.value === 'string' ? parseFloat(params.value) : params.value
              if (isNaN(num)) return '$0'
              return '$' + Math.round(num).toLocaleString('en-US')
            },
            getQuickFilterText: params => {
              return Math.round(params.data['stores_reco_qty'] * params.data['retail_price_($)'])
            },
            filterValueGetter: params => {
              return Math.round(params.data['stores_reco_qty'] * params.data['retail_price_($)'])
            }
          }
        case 'total_reco_price_($)':
          return {
            valueGetter: (params: ValueGetterParams) => {
              console.log('row for sum valueGetter', params)
              // depending on other calculations doesn't always work
              return (
                (params.data['ecomm_reco_qty'] + params.data['stores_reco_qty']) *
                                params.data['retail_price_($)']
              )
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.data?.__isTotalRow) return '$' + params.data[col.colId].toLocaleString('en-US')
              if (params.value == null || params.value === '') return '$0'
              const num = typeof params.value === 'string' ? parseFloat(params.value) : params.value
              if (isNaN(num)) return '$0'
              return '$' + Math.round(num).toLocaleString('en-US')
            },
            getQuickFilterText: params => {
              return Math.round(
                (params.data['ecomm_reco_qty'] + params.data['stores_reco_qty']) *
                                    params.data['retail_price_($)']
              )
            },
            filterValueGetter: params => {
              return Math.round(
                (params.data['ecomm_reco_qty'] + params.data['stores_reco_qty']) *
                                    params.data['retail_price_($)']
              )
            }
          }
          // case 'status':
          //   return {
          //     valueFormatter: params => {
          //       if (params?.data?.error_reason) {
          //         return params?.data?.error_reason
          //       }
          //       return params.value
          //     }
          //   }
        case 'delete':
          return {
            cellRendererSelector: (params: ICellRendererParams) => {
              if (params.data?.__isTotalRow) return ButtonCellRenderer
              return !params.data ? ButtonCellRenderer : null
            },
            cellRendererParams: (params: ICellRendererParams) => {
              return {
                disabled: false,
                toolTipMessage: 'Delete Product Details',
                onClick: (params: CellClickedEvent) => params.context.deletePlanLine(params)
              }
            }
          }
        default:
          if (col.refData.datatype === 'currency') {
            return {
              valueFormatter: (params: ValueFormatterParams) => {
                if (params.data?.__isTotalRow) return null
                if (!params.value) return '$0'
                const num = typeof params.value === 'string' ? parseFloat(params.value) : params.value
                if (isNaN(num)) return '$0'

                const formatted = Math.round(num).toLocaleString('en-US')

                return '$' + formatted
              },
              getQuickFilterText: params => {
                console.log('params for getQuickFilterText', params)
                if (params.data?.__isTotalRow) return null
                return Math.round(params.data[params.colDef.field])
              },
              filterValueGetter: params => {
                console.log('params for filterValueGetter', params)
                if (params.data?.__isTotalRow) return null
                return Math.round(params.data[params.colDef.field])
              }
            }
          }
          if (col.refData.datatype === 'number' || col.refData.datatype === 'integer') {
            console.log('col', col)
            return {
              valueFormatter: (params: ValueFormatterParams) => {
                return Math.round(params.value).toLocaleString('en-US')
              },
              getQuickFilterText: params => {
                return Math.round(params.value)
              },
              filterValueGetter: params => {
                return Math.round(params.value)
              }
            }
          }
          return {
            valueFormatter: (params: ValueFormatterParams) => params.value
          }
      }
    }
    // Detail Grid Settings
    // if (gridToRender === 'detail') {
    // }
  }
  const updatedCol = {
    ...col,
    ...getParams(),
    cellClassRules: {
      ...CellClassRules
    }
  }
  return updatedCol
}
